/**
 * Типизированные переменные клиента + персональная скидка.
 * Монтируется только для manager+ (см. ClientProfilePage_vm) — у operator
 * нет доступа к Resource.CLIENTS вообще (backend/app/core/permissions.py),
 * поэтому для него вкладка не рендерится и запросы не уходят.
 *
 * CAS-контракт (см. аудит D:\claude dev\work\dlvry-zhorka-audit-20261017):
 * каждая запись несёт expected_version, сервер инкрементит version ровно на
 * 1 при КАЖДОЙ записи (включая запись того же значения). GET-после-ошибки
 * никогда не доказывает отсутствие записи — поэтому фрозен-pending хранится
 * в localStorage (useClientVariableAttempt.ts) до позитивного known-contract
 * ACK или до строго более новой версии на фреш-GET при 409.
 *
 * Персональная скидка — User.meta['personal_discount'] + отдельная
 * personal_discount_version. 0/false/'' — валидные явные значения, не
 * "пусто"; "не введено" — только undefined. Неактивные определения —
 * read-only (историческое значение видно, редактирование заблокировано).
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Form, Input, InputNumber, Modal,
  Select, Space, Table, Tag, Typography, message,
} from 'antd';
import dayjs from 'dayjs';
import {
  getPersonalDiscount, listClientVariableValues, listVariableDefinitions,
} from '../services/customerCareApi';
import type { ClientVariableDefinition, ClientVariableValueType } from '../services/customerCareApi';
import { useScenarioScope } from '../pages/marketing/useScenarioScope';
import {
  parsePersonalDiscount, parseVariableValueRow,
  useVariableDefinitionCreateAttempt, usePersonalDiscountAttempt, useVariableValueAttempt,
} from './useClientVariableAttempt';
import type { PersonalDiscountSnapshot, VariableValueRow } from './useClientVariableAttempt';
import type { StoredDefinitionCreateBody } from './clientVariableAttemptStorage';

type ScenarioScope = ReturnType<typeof useScenarioScope>;

const { Text } = Typography;

const TYPE_LABELS: Record<ClientVariableValueType, string> = {
  string: 'Текст', int: 'Число', bool: 'Да/Нет', date: 'Дата', enum: 'Список значений',
};

function describeVarError(code: string | null): string {
  if (code === 'storage_unavailable') return 'Локальное хранилище недоступно.';
  if (code === 'corrupt_marker') return 'Повреждённые локальные данные о попытке.';
  if (code === 'invalid_response') return 'Сервер вернул неожиданный ответ.';
  return 'Результат предыдущей попытки неизвестен.';
}

// Без JS rollover (new Date(2023,1,30) молча становится 2 марта) — строго
// проверяем, что компоненты даты пережили конструирование без изменений.
function isStrictIsoDate(s: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return false;
  const year = Number(m[1]), month = Number(m[2]), day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const dt = new Date(Date.UTC(year, month - 1, day));
  return dt.getUTCFullYear() === year && dt.getUTCMonth() === month - 1 && dt.getUTCDate() === day;
}

type TypedDraftResult =
  | { ok: true; value: string | number | boolean }
  | { ok: false; message: string };

// "Не введено" = undefined для ВСЕХ типов. 0/false/'' — валидные явные
// значения, никогда не блокируют сохранение.
function validateTypedDraft(def: ClientVariableDefinition, draft: string | number | boolean | undefined): TypedDraftResult {
  if (draft === undefined) return { ok: false, message: 'Введите значение' };
  switch (def.value_type) {
    case 'string':
      return typeof draft === 'string' ? { ok: true, value: draft } : { ok: false, message: 'Введите значение' };
    case 'int': {
      const n = typeof draft === 'number' ? draft : Number(draft);
      return Number.isSafeInteger(n) ? { ok: true, value: n } : { ok: false, message: 'Введите целое число' };
    }
    case 'bool':
      return typeof draft === 'boolean' ? { ok: true, value: draft } : { ok: false, message: 'Выберите Да или Нет' };
    case 'date':
      return (typeof draft === 'string' && isStrictIsoDate(draft)) ? { ok: true, value: draft } : { ok: false, message: 'Введите корректную дату' };
    case 'enum':
      return (typeof draft === 'string' && (def.enum_choices || []).includes(draft)) ? { ok: true, value: draft } : { ok: false, message: 'Выберите значение из списка' };
    default:
      return { ok: false, message: 'Неизвестный тип' };
  }
}

/** Один ряд таблицы — СОБСТВЕННЫЙ экземпляр useVariableValueAttempt,
 * привязанный к definition.id через props (симметрично DefinitionRow из
 * ClientStateDefinitionsPage.tsx) — иначе обработчик клика мог бы увидеть
 * устаревший id из закрытия предыдущего рендера. */
function VariableRow({
  scope, userId, definition, row, valuesLoadError, onChanged,
}: {
  scope: ScenarioScope; userId: number; definition: ClientVariableDefinition;
  row: VariableValueRow | null; valuesLoadError: boolean; onChanged: () => void;
}) {
  const attempt = useVariableValueAttempt(scope, userId, definition.id);
  const [draft, setDraft] = useState<string | number | boolean | undefined>(row?.value);
  // Версия, которую сейчас "знает" этот ряд — обновляется при приходе
  // свежего row (новая загрузка) или явном подтверждении конфликта.
  const knownVersionRef = useRef<number>(row?.version ?? 0);

  useEffect(() => {
    setDraft(row?.value);
    knownVersionRef.current = row?.version ?? 0;
  }, [row]);

  useEffect(() => {
    if (attempt.status === 'success' && attempt.success) onChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt.status, attempt.success]);

  const save = async (expectedVersion: number) => {
    const validation = validateTypedDraft(definition, draft);
    if (!validation.ok) { message.error(validation.message); return; }
    await attempt.submit({ value: validation.value, expected_version: expectedVersion });
  };

  const disabled = !definition.is_active || valuesLoadError;

  let input: React.ReactNode;
  switch (definition.value_type) {
    case 'bool':
      input = (
        <Select
          style={{ minWidth: 120 }}
          placeholder="Выберите"
          disabled={disabled}
          value={draft as boolean | undefined}
          options={[{ value: true, label: 'Да' }, { value: false, label: 'Нет' }]}
          onChange={(v: boolean) => setDraft(v)}
        />
      );
      break;
    case 'int':
      input = (
        <InputNumber
          disabled={disabled}
          value={draft as number | undefined}
          onChange={(v) => setDraft(v === null ? undefined : v)}
        />
      );
      break;
    case 'date':
      input = (
        <DatePicker
          disabled={disabled}
          value={draft ? dayjs(draft as string) : undefined}
          onChange={(d) => setDraft(d ? d.format('YYYY-MM-DD') : undefined)}
        />
      );
      break;
    case 'enum':
      input = (
        <Select
          style={{ minWidth: 160 }}
          disabled={disabled}
          value={draft as string | undefined}
          options={(definition.enum_choices || []).map((c) => ({ value: c, label: c }))}
          onChange={(v: string) => setDraft(v)}
        />
      );
      break;
    default:
      input = (
        <Input
          disabled={disabled}
          value={draft as string | undefined}
          maxLength={1024}
          onChange={(e) => setDraft(e.target.value)}
        />
      );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space>
        {input}
        <Button size="small" disabled={disabled || attempt.status === 'blocked'}
          loading={attempt.status === 'submitting'} onClick={() => save(knownVersionRef.current)}>
          Сохранить
        </Button>
        {!definition.is_active && <Tag>Неактивно</Tag>}
      </Space>
      {attempt.status === 'pending' && (
        <Alert type="warning" showIcon message={describeVarError(attempt.errorCode)}
          description={<Button size="small" onClick={() => attempt.retry()}>Повторить тот же запрос</Button>} />
      )}
      {attempt.status === 'conflict' && attempt.conflictValue && (
        <Alert type="warning" showIcon message="Изменено кем-то другим."
          description={
            <Space direction="vertical">
              <span>Текущее значение на сервере: {String(attempt.conflictValue.value)}, версия {attempt.conflictValue.version}.</span>
              <Button size="small" onClick={() => save(attempt.conflictValue!.version)}>Сохранить моё значение с актуальной версии</Button>
            </Space>
          } />
      )}
      {attempt.status === 'blocked' && <Alert type="error" showIcon message={describeVarError(attempt.errorCode)} />}
    </Space>
  );
}

/** Персональная скидка — отдельная карточка со своим CAS-циклом записи.
 * ВАЖНО: начальная загрузка {value, version} ЗДЕСЬ больше не живёт — если бы
 * этот компонент (ребёнок ClientVariablesTab) сам запускал scope-gated GET в
 * СВОЁМ mount-эффекте, тот эффект детерминированно commit'ится РАНЬШЕ, чем
 * эффект useScenarioScope() у родителя (React коммитит эффекты потомков до
 * эффектов родителя) — а значит raньше, чем scope выставит mounted.current,
 * и requestConfig()/isCurrent() синхронно бросят "Stale admin context" на
 * самом первом монтировании. Поэтому загрузку поднял в ClientVariablesTab
 * (где useScenarioScope() вызван в ТОМ ЖЕ компоненте и его эффект гарантированно
 * коммитится первым) и прокидываю снимок сюда пропсами; этот компонент только
 * пишет (CAS PATCH), что безопасно — submit/retry вызываются по клику, уже
 * после того как все эффекты монтирования отработали. GET-ошибка/отсутствующая
 * версия ВСЕГДА блокирует Save, никогда не подставляет 0/null по умолчанию. */
function DiscountCard({
  scope, userId, snapshot, loading, loadError, onRetryLoad, onSnapshotChange,
}: {
  scope: ScenarioScope; userId: number; snapshot: PersonalDiscountSnapshot | null;
  loading: boolean; loadError: boolean; onRetryLoad: () => void;
  onSnapshotChange: (next: PersonalDiscountSnapshot) => void;
}) {
  const attempt = usePersonalDiscountAttempt(scope, userId);
  const [draft, setDraft] = useState<number | null>(snapshot?.personalDiscount ?? null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setDraft(snapshot?.personalDiscount ?? null);
    setTouched(false);
  }, [snapshot]);

  useEffect(() => {
    if (attempt.status === 'success' && attempt.success) {
      onSnapshotChange(attempt.success);
      setTouched(false);
      message.success('Персональная скидка сохранена');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt.status, attempt.success]);

  const saveWithVersion = async (expectedVersion: number) => {
    await attempt.submit({ personal_discount: draft, expected_version: expectedVersion });
  };

  const disabled = loadError || !snapshot;

  return (
    <Card size="small" title="Персональная скидка" style={{ marginBottom: 16 }}>
      {loadError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Не удалось загрузить текущую скидку. Сохранение отключено, чтобы не затереть серверное значение неизвестным."
          action={<Button size="small" onClick={onRetryLoad}>Повторить</Button>}
        />
      )}
      <Space>
        <InputNumber
          min={0}
          max={100}
          placeholder="0–100"
          disabled={loading || disabled}
          value={draft ?? undefined}
          onChange={(v) => { setDraft(v ?? null); setTouched(true); }}
        />
        <span>%</span>
        <Button
          type="primary"
          loading={attempt.status === 'submitting' || loading}
          disabled={disabled || !touched || attempt.status === 'blocked'}
          onClick={() => snapshot && saveWithVersion(snapshot.version)}
        >
          Сохранить скидку
        </Button>
      </Space>
      {attempt.status === 'pending' && (
        <Alert type="warning" showIcon style={{ marginTop: 12 }} message={describeVarError(attempt.errorCode)}
          description={<Button size="small" onClick={() => attempt.retry()}>Повторить тот же запрос</Button>} />
      )}
      {attempt.status === 'conflict' && attempt.conflictValue && (
        <Alert type="warning" showIcon style={{ marginTop: 12 }} message="Изменено кем-то другим."
          description={
            <Space direction="vertical">
              <span>Текущее значение на сервере: {attempt.conflictValue.personalDiscount ?? '—'}%, версия {attempt.conflictValue.version}.</span>
              <Button size="small" onClick={() => saveWithVersion(attempt.conflictValue!.version)}>Сохранить моё значение с актуальной версии</Button>
            </Space>
          } />
      )}
      {attempt.status === 'blocked' && <Alert type="error" showIcon style={{ marginTop: 12 }} message={describeVarError(attempt.errorCode)} />}
    </Card>
  );
}

interface Props {
  userId: number;
}

const ClientVariablesContent: React.FC<Props> = ({ userId }) => {
  const scope = useScenarioScope();

  const [definitions, setDefinitions] = useState<ClientVariableDefinition[]>([]);
  const [rows, setRows] = useState<Record<number, VariableValueRow>>({});
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [discountSnapshot, setDiscountSnapshot] = useState<PersonalDiscountSnapshot | null>(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountLoadError, setDiscountLoadError] = useState(false);
  const [discountReloadToken, setDiscountReloadToken] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [newDefForm] = Form.useForm<{ key: string; label: string; value_type: ClientVariableValueType; enum_choices?: string }>();
  const watchedKey: string = Form.useWatch('key', newDefForm) || '';
  const newDefType: ClientVariableValueType = Form.useWatch('value_type', newDefForm) || 'string';
  const createAttempt = useVariableDefinitionCreateAttempt(scope, watchedKey);

  useEffect(() => {
    if (!scope.enabled) { setDefinitions([]); setRows({}); setLoading(false); setListError(false); return undefined; }
    const token = scope.capture();
    const controller = new AbortController();
    const abort = () => controller.abort();
    token.controller.signal.addEventListener('abort', abort);
    if (token.controller.signal.aborted) abort();
    let alive = true;
    setLoading(true);
    void (async () => {
      try {
        const requestConfig = scope.requestConfig(token);
        const [defs, rawRows] = await Promise.all([
          listVariableDefinitions({ ...requestConfig, signal: controller.signal }),
          listClientVariableValues(userId, { ...requestConfig, signal: controller.signal }),
        ]);
        if (!alive || !scope.isCurrent(token)) return;
        // Список определений показываем независимо от валидности строк
        // значений — админ должен видеть, какие переменные существуют, даже
        // если сохранение временно заблокировано из-за плохих версий.
        setDefinitions(defs);
        const parsedRows = rawRows.map(parseVariableValueRow);
        // Присутствующая строка без корректной версии — ошибка загрузки, а
        // не "версия 0": отсутствие строки означает версию 0, малформенная
        // строка — нет. Блокируем ТОЛЬКО сохранение (valuesLoadError), не
        // прячем саму таблицу определений.
        if (parsedRows.some((r) => r === null)) {
          setRows({});
          setListError(true);
        } else {
          const byDef: Record<number, VariableValueRow> = {};
          (parsedRows as VariableValueRow[]).forEach((r) => { byDef[r.definitionId] = r; });
          setRows(byDef);
          setListError(false);
        }
      } catch {
        if (alive && scope.isCurrent(token)) setListError(true);
      } finally {
        if (alive && scope.isCurrent(token)) setLoading(false);
      }
    })();
    return () => { alive = false; controller.abort(); token.controller.signal.removeEventListener('abort', abort); };
  }, [scope, userId, reloadToken]);

  // Загрузка скидки — ЗДЕСЬ, а не в DiscountCard: этот эффект объявлен в том
  // же компоненте, что и useScenarioScope(), поэтому React коммитит эффект
  // самого scope ПЕРЕД этим (эффекты одного компонента — в порядке вызова
  // хуков), и requestConfig()/isCurrent() уже видят mounted.current=true на
  // первом монтировании. См. комментарий у DiscountCard.
  useEffect(() => {
    if (!scope.enabled) { setDiscountSnapshot(null); setDiscountLoading(false); setDiscountLoadError(false); return undefined; }
    const token = scope.capture();
    const controller = new AbortController();
    const abort = () => controller.abort();
    token.controller.signal.addEventListener('abort', abort);
    if (token.controller.signal.aborted) abort();
    let alive = true;
    setDiscountSnapshot(null);
    setDiscountLoading(true);
    setDiscountLoadError(false);
    void (async () => {
      try {
        const requestConfig = scope.requestConfig(token);
        const response = await getPersonalDiscount(userId, { ...requestConfig, signal: controller.signal });
        if (!alive || !scope.isCurrent(token)) return;
        const parsed = parsePersonalDiscount(response);
        if (!parsed) throw new Error('bad shape');
        setDiscountSnapshot(parsed);
        setDiscountLoadError(false);
      } catch {
        if (alive && scope.isCurrent(token)) { setDiscountSnapshot(null); setDiscountLoadError(true); }
      } finally {
        if (alive && scope.isCurrent(token)) setDiscountLoading(false);
      }
    })();
    return () => { alive = false; controller.abort(); token.controller.signal.removeEventListener('abort', abort); };
  }, [scope, userId, discountReloadToken]);

  useEffect(() => {
    if (createAttempt.status === 'ready' && createAttempt.created) {
      setCreateOpen(false);
      newDefForm.resetFields();
      setReloadToken((x) => x + 1);
      message.success('Переменная создана');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createAttempt.status, createAttempt.created]);

  const openCreate = () => {
    newDefForm.resetFields();
    setCreateOpen(true);
  };

  const onCreateSubmit = async () => {
    // Захват scope ДО (потенциально асинхронной) валидации формы, повторная
    // проверка ПОСЛЕ неё и ПЕРЕД записью — симметрично ClientStateDefinitionsPage.
    const token = scope.capture();
    const values = await newDefForm.validateFields();
    if (!scope.isCurrent(token)) return;
    const body: StoredDefinitionCreateBody = {
      key: values.key,
      label: values.label,
      value_type: values.value_type,
      enum_choices: values.value_type === 'enum'
        ? (values.enum_choices || '').split(',').map((s) => s.trim()).filter(Boolean)
        : null,
    };
    await createAttempt.create(body);
  };

  return (
    <div>
      <DiscountCard
        scope={scope} userId={userId}
        snapshot={discountSnapshot} loading={discountLoading} loadError={discountLoadError}
        onRetryLoad={() => setDiscountReloadToken((x) => x + 1)}
        onSnapshotChange={setDiscountSnapshot}
      />

      <Card
        size="small"
        title="Переменные клиента"
        extra={<Button size="small" onClick={openCreate} disabled={!scope.enabled}>+ Добавить переменную</Button>}
      >
        {listError && (
          <Alert type="error" message="Не удалось загрузить список переменных." style={{ marginBottom: 16 }}
            action={<Button size="small" onClick={() => setReloadToken((x) => x + 1)}>Повторить</Button>} />
        )}

        {/* Видно независимо от того, открыта ли модалка создания — предыдущая
            неизвестная/409 попытка должна оставаться видимой без переоткрытия
            модалки и переживать reload/remount (localStorage). */}
        {createAttempt.status === 'pending' && (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }}
            message={`Незавершённая попытка создания «${createAttempt.pendingBody?.key ?? ''}».`}
            description={
              <Space direction="vertical">
                <span>
                  {describeVarError(createAttempt.errorCode === 'duplicate_key' ? null : createAttempt.errorCode)}
                  {createAttempt.errorCode === 'duplicate_key' && ' Ключ уже занят.'}
                  {createAttempt.conflictDefinition && (
                    <> Существующее определение: «{createAttempt.conflictDefinition.label}», тип «{TYPE_LABELS[createAttempt.conflictDefinition.valueType]}».</>
                  )}
                </span>
                <Button size="small" onClick={() => createAttempt.retry()}>Повторить тот же запрос</Button>
              </Space>
            }
          />
        )}
        {createAttempt.status === 'blocked' && (
          <Alert type="error" showIcon style={{ marginBottom: 16 }} message={describeVarError(createAttempt.errorCode)} />
        )}

        <Table<ClientVariableDefinition>
          rowKey="id"
          loading={loading}
          dataSource={definitions}
          pagination={false}
          size="small"
          columns={[
            { title: 'Переменная', dataIndex: 'label' },
            { title: 'Ключ', dataIndex: 'key', width: 160, render: (v: string) => <Text code>{v}</Text> },
            { title: 'Тип', dataIndex: 'value_type', width: 140, render: (v: ClientVariableValueType) => TYPE_LABELS[v] },
            {
              title: 'Статус', key: 'status', width: 110,
              render: (_: unknown, row: ClientVariableDefinition) => (row.is_active ? <Tag color="green">Активно</Tag> : <Tag>Неактивно</Tag>),
            },
            {
              title: 'Значение', key: 'value',
              render: (_: unknown, def: ClientVariableDefinition) => (
                <VariableRow key={def.id} scope={scope} userId={userId} definition={def}
                  row={rows[def.id] ?? null} valuesLoadError={listError} onChanged={() => setReloadToken((x) => x + 1)} />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="Новая переменная клиента"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreateSubmit}
        okText="Создать"
        confirmLoading={createAttempt.status === 'submitting'}
        destroyOnClose
      >
        <Form form={newDefForm} layout="vertical">
          <Form.Item
            name="key"
            label="Ключ (латиница, snake_case)"
            rules={[{ required: true, pattern: /^[a-z][a-z0-9_]{0,63}$/, message: 'Формат: ^[a-z][a-z0-9_]{0,63}$' }]}
          >
            <Input placeholder="favorite_dish" />
          </Form.Item>
          <Form.Item name="label" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
            <Input placeholder="Любимое блюдо" />
          </Form.Item>
          <Form.Item name="value_type" label="Тип значения" rules={[{ required: true }]} initialValue="string">
            <Select
              options={(Object.keys(TYPE_LABELS) as ClientVariableValueType[]).map((t) => ({ value: t, label: TYPE_LABELS[t] }))}
            />
          </Form.Item>
          {newDefType === 'enum' && (
            <Form.Item name="enum_choices" label="Варианты (через запятую)" rules={[{ required: true, message: 'Укажите варианты' }]}>
              <Input placeholder="Вариант 1, Вариант 2" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

// A customer is a separate subject even when the administrative actor stays
// the same. Unmount the old scope so its late writes cannot update this one.
const ClientVariablesTab: React.FC<Props> = ({ userId }) => (
  <ClientVariablesContent key={userId} userId={userId} />
);
export default ClientVariablesTab;
